import apiClient from './apiClient';
import { AuditTransformer } from './auditTransformer';
import { AuditSessionManager } from './auditSessionManager';
import { AuditPerformanceMonitor } from './auditPerformanceMonitor';
import { AuditDebugger } from './auditDebugger';

// Enhanced Audit Service for managing independent audit responses
class AuditService {
  constructor() {
    this.currentAuditResponse = null;
    this.isDraftMode = false;
    this.saveQueue = new Map(); // Queue for pending saves
    this.lastSaveTimestamp = new Map(); // Track last save times
    this.independentMode = true; // Flag for independent audit management
    
    // Initialize sub-modules
    this.transformer = new AuditTransformer();
    this.sessionManager = new AuditSessionManager();
    this.performanceMonitor = new AuditPerformanceMonitor(this);
    this.debugger = new AuditDebugger(this);
  }

  // 🔥 NEW: Fetch all draft audits for the current user from backend
  // Replace the fetchAllDraftAudits method in auditService.js with this fixed version

// Updated method in auditService.js
async fetchAllDraftAudits(userId) {
    try {
      console.log('Fetching all audit data with statistics from backend...');
      console.log('User/Operator ID:', userId);

      // Build the URL with query parameters. When userId is missing
      // (e.g. logged in as admin without an operator profile) we omit
      // the operator filter so the dashboard still shows recent audits.
      const queryParams = new URLSearchParams({
        page_size: '100',
        include_stats: 'true',
      });
      if (userId) {
        queryParams.set('operator', userId);
      }
      
      const url = `/audit-responses/?${queryParams.toString()}`;
      console.log('Fetching URL:', url);
      
      // Call the backend API
      const response = await apiClient.get(url);

      if (response.ok) {
        const data = await response.json();
        console.log(`API Response - Found ${data.data?.length || 0} audit responses`);
        console.log('Statistics:', data.statistics);
        
        return {
          success: true,
          drafts: data.data || [],
          totalCount: data.pagination?.total_items || 0,
          statistics: data.statistics || null // Include statistics
        };
      }
      
      const errorData = await response.json();
      console.error('API Error:', errorData);
      return {
        success: false,
        error: errorData.error || 'Failed to fetch audit data',
        drafts: [],
        statistics: null
      };
    } catch (error) {
      console.error('Error fetching audit data from backend:', error);
      return {
        success: false,
        error: 'Network error while fetching audit data',
        drafts: [],
        statistics: null
      };
    }
  }
  // 🔥 ENHANCED: Create a new independent audit response
  async createAuditResponse(auditFormId, operatorId, deviceId = 'WEB_CLIENT', shift = '', workOrder = '') {
    try {
      console.log('🚀 ENHANCED: Creating independent audit response...');
      console.log(`Audit Form ID: ${auditFormId}`);
      console.log(`Operator ID: ${operatorId}`);
      console.log(`Device ID: ${deviceId}`);
      
      // Generate unique session identifier for this audit
      const sessionId = `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const response = await apiClient.post('/api/audit-responses/create/', {
        audit_form_id: auditFormId,
        operator_id: operatorId,
        device_id: deviceId,
        shift: shift,
        work_order: workOrder,
        raw_response_json: {
          sessionId: sessionId,
          createdAt: new Date().toISOString(),
          isIndependent: true,
          cumulativeAuditData: {}
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.message && data.data) {
          this.currentAuditResponse = data.data;
          this.isDraftMode = true;
          
          // Store with session-specific keys for independent management
          this.sessionManager.storeSession(sessionId, data.data, data.session_id);
          
          console.log('✅ Independent audit response created successfully');
          console.log(`Session ID: ${sessionId}`);
          console.log(`Response ID: ${data.data.audit_form_response_id}`);
          
          return {
            success: true,
            auditResponse: data.data,
            sessionId: sessionId,
            backendSessionId: data.session_id,
            message: data.message,
            isIndependent: true
          };
        }
      }
      
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || 'Failed to create audit response'
      };
    } catch (error) {
      console.error('❌ Error creating independent audit response:', error);
      return {
        success: false,
        error: 'Network error while creating audit response'
      };
    }
  }

  // 🔥 ENHANCED: Update with better independent audit handling
  async updateAuditResponse(responseId, updateData, options = {}) {
    const { 
      immediate = false,
      calculateScore = true,
      skipQueue = false
    } = options;

    const saveKey = `${responseId}_${Date.now()}`;
    
    try {
      console.log(`💾 ENHANCED: Updating independent audit response ${responseId}...`);
      
      // Add session tracking to update data
      const currentSessionId = this.sessionManager.getCurrentSessionId();
      if (currentSessionId && updateData.raw_response_json) {
        updateData.raw_response_json.sessionId = currentSessionId;
        updateData.raw_response_json.lastUpdated = new Date().toISOString();
        updateData.raw_response_json.isIndependent = true;
      }
      
      // Add to save queue if not immediate
      if (!immediate && !skipQueue) {
        this.saveQueue.set(saveKey, { responseId, updateData, timestamp: Date.now() });
        
        // Debounced save after 2 seconds
        setTimeout(() => {
          if (this.saveQueue.has(saveKey)) {
            this.processSaveQueue(responseId);
          }
        }, 2000);
        
        return {
          success: true,
          queued: true,
          message: 'Update queued for save'
        };
      }

      // Calculate real-time score if enabled
      if (calculateScore && updateData.raw_response_json?.cumulativeAuditData) {
        const scoreData = await this.calculateRealtimeScore(responseId, updateData.raw_response_json.cumulativeAuditData);
        if (scoreData.success) {
          console.log(`📊 Real-time score: ${scoreData.score_data.score_percentage}%`);
          
          updateData.total_points_achieved = scoreData.score_data.total_points_achieved;
          updateData.score_percentage = scoreData.score_data.score_percentage;
        }
      }

      const response = await apiClient.put(`/api/audit-responses/${responseId}/update/`, updateData);

      if (response.ok) {
        const data = await response.json();
        if (data.message && data.data) {
          this.currentAuditResponse = data.data;
          this.lastSaveTimestamp.set(responseId, Date.now());
          
          // Update session storage
          this.sessionManager.updateCurrentSession(data.data);
          
          console.log(`✅ Independent audit response updated successfully`);
          if (data.score_info) {
            console.log(`📊 Score: ${data.score_info.current_score}% (${data.score_info.total_achieved}/${data.score_info.total_possible})`);
          }
          
          return {
            success: true,
            auditResponse: data.data,
            scoreInfo: data.score_info,
            message: data.message,
            isIndependent: true
          };
        }
      }
      
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || 'Failed to update audit response'
      };
    } catch (error) {
      console.error('❌ Error updating independent audit response:', error);
      return {
        success: false,
        error: 'Network error while updating audit response'
      };
    }
  }

  // Process queued saves
  async processSaveQueue(responseId) {
    const queuedSaves = Array.from(this.saveQueue.entries())
      .filter(([key, data]) => data.responseId === responseId)
      .sort((a, b) => b[1].timestamp - a[1].timestamp); // Latest first

    if (queuedSaves.length === 0) return;

    // Get the most recent save data
    const [latestKey, latestData] = queuedSaves[0];
    
    // Clear all queued saves for this response
    queuedSaves.forEach(([key]) => this.saveQueue.delete(key));

    console.log(`🚀 Processing queued save for independent response ${responseId}`);
    
    // Execute the save
    return await this.updateAuditResponse(responseId, latestData.updateData, { 
      immediate: true, 
      skipQueue: true 
    });
  }

  // Calculate real-time score without saving
  async calculateRealtimeScore(responseId, cumulativeAuditData) {
    try {
      const response = await apiClient.post(`/api/audit-responses/${responseId}/calculate-score/`, {
        cumulative_audit_data: cumulativeAuditData
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      }
      
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || 'Failed to calculate score'
      };
    } catch (error) {
      console.error('Error calculating real-time score:', error);
      return {
        success: false,
        error: 'Network error while calculating score'
      };
    }
  }

  // 🔥 ENHANCED: Complete independent audit response
  async completeAuditResponse(responseId, finalRemarks, fieldResponses) {
    try {
      console.log('🚀 ENHANCED: Completing independent audit...');
      console.log(`Response ID: ${responseId}`);
      console.log(`Field responses count: ${fieldResponses.length}`);
      console.log(`Final remarks: ${finalRemarks}`);
      
      if (!responseId) {
        throw new Error('Response ID is required');
      }
      
      if (!fieldResponses || fieldResponses.length === 0) {
        console.warn('⚠️ No field responses provided - this will create a perfect score audit');
      }
      
      // Add completion metadata
      const currentSessionId = this.sessionManager.getCurrentSessionId();
      const completionData = {
        final_remarks: finalRemarks || 'Independent audit completed',
        field_responses: fieldResponses || [],
        completion_metadata: {
          sessionId: currentSessionId,
          completedAt: new Date().toISOString(),
          isIndependent: true,
          fieldResponseCount: fieldResponses.length
        }
      };
      
      console.log('📤 Sending independent completion data:', completionData);
      
      const response = await apiClient.post(`/api/audit-responses/${responseId}/complete/`, completionData);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📥 Independent completion response:', data);
        
        if (data.message && data.audit_response) {
          this.currentAuditResponse = data.audit_response;
          this.isDraftMode = false;
          
          // Clean up session-specific storage
          this.sessionManager.cleanupCompletedSession(currentSessionId);
          
          console.log('✅ Independent audit completion successful');
          return {
            success: true,
            auditResponse: data.audit_response,
            fieldResponses: data.field_responses,
            scoringSummary: data.scoring_summary,
            message: data.message,
            isIndependent: true
          };
        } else {
          throw new Error(data.error || 'Unexpected response format');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ ENHANCED: Error completing independent audit:', error);
      return {
        success: false,
        error: error.message || 'Failed to complete independent audit response'
      };
    }
  }

  // 🔥 ENHANCED: Get audit response with session awareness
  async getAuditResponse(responseId, options = {}) {
    const { useCache = false } = options;
    
    try {
      console.log(`🔍 Fetching independent audit response ${responseId} from backend...`);
      
      const response = await apiClient.get(`/api/audit-responses/${responseId}/`);

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          // Update current response if it's the same ID
          if (this.currentAuditResponse?.audit_form_response_id === responseId) {
            this.currentAuditResponse = data.data;
            this.sessionManager.updateCurrentSession(data.data);
          }
          
          console.log(`✅ Fetched fresh independent audit response data`);
          console.log(`📊 Categories: ${data.data.categories_hierarchy?.length || 0}, Field responses: ${data.data.field_responses?.length || 0}`);
          
          return {
            success: true,
            auditResponse: data.data,
            isIndependent: true
          };
        }
      }
      
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || 'Failed to get audit response'
      };
    } catch (error) {
      console.error('Error getting independent audit response:', error);
      return {
        success: false,
        error: 'Network error while getting audit response'
      };
    }
  }

  // 🔥 ENHANCED: Check for drafts - NOW FETCHES FROM BACKEND
  async checkForDraftAudits(userId) {
    try {
      console.log('🔍 ENHANCED: Checking for all draft audits from backend...');
      
      // First, fetch all drafts from the backend
      const backendResult = await this.fetchAllDraftAudits(userId);
      
      if (!backendResult.success) {
        console.error('❌ Failed to fetch drafts from backend:', backendResult.error);
        return {
          success: false,
          error: backendResult.error,
          hasDraft: false,
          drafts: []
        };
      }
      
      const drafts = backendResult.drafts;
      
      if (drafts.length > 0) {
        console.log(`✅ Found ${drafts.length} draft audits from backend`);
        
        // Check if we have a current session in localStorage
        const currentSessionId = localStorage.getItem('currentAuditSessionId');
        const currentResponseId = this.currentAuditResponse?.audit_form_response_id;
        
        // Find if any of the backend drafts match our current session
        let currentDraft = null;
        if (currentResponseId) {
          currentDraft = drafts.find(draft => 
            draft.audit_form_response_id === currentResponseId
          );
        }
        
        // If we have a current draft, make sure it's set as current
        if (currentDraft) {
          this.currentAuditResponse = currentDraft;
          this.isDraftMode = true;
        }
        
        return {
          success: true,
          hasDraft: true,
          drafts: drafts, // Return ALL drafts
          currentDraft: currentDraft,
          totalDrafts: drafts.length,
          sessionId: currentSessionId
        };
      }
      
      console.log('📝 No draft audits found in backend');
      
      // Clear any stale localStorage data
      this.sessionManager.clearAllDraftData();
      
      return {
        success: true,
        hasDraft: false,
        drafts: [],
        totalDrafts: 0
      };
    } catch (error) {
      console.error('❌ Error checking for draft audits:', error);
      return {
        success: false,
        error: 'Failed to check for draft audits',
        hasDraft: false,
        drafts: []
      };
    }
  }

  // 🔥 NEW: Resume a specific draft audit
  async resumeDraftAudit(draftAuditResponse) {
    try {
      console.log('🔄 Resuming draft audit:', draftAuditResponse.audit_form_response_id);
      
      // Fetch the latest data for this draft from backend
      const result = await this.getAuditResponse(draftAuditResponse.audit_form_response_id);
      
      if (result.success && result.auditResponse.status === 'DRAFT') {
        // Set as current audit response
        this.currentAuditResponse = result.auditResponse;
        this.isDraftMode = true;
        
        // Create a new session for this resumed draft
        const sessionId = `audit_resumed_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // Store in session manager
        this.sessionManager.storeSession(
          sessionId, 
          result.auditResponse, 
          result.auditResponse.response_session_id
        );
        
        console.log('✅ Successfully resumed draft audit');
        
        return {
          success: true,
          auditResponse: result.auditResponse,
          sessionId: sessionId,
          message: 'Draft audit resumed successfully'
        };
      } else {
        throw new Error('Draft no longer exists or has been completed');
      }
    } catch (error) {
      console.error('❌ Error resuming draft audit:', error);
      return {
        success: false,
        error: error.message || 'Failed to resume draft audit'
      };
    }
  }

  // 🔥 NEW: Get summary of all drafts
  async getDraftsSummary(userId) {
    try {
      const result = await this.fetchAllDraftAudits(userId);
      
      if (!result.success) {
        return {
          success: false,
          error: result.error,
          summary: []
        };
      }
      
      // Create summary for each draft
      const summary = result.drafts.map(draft => ({
        id: draft.audit_form_response_id,
        formName: draft.audit_form?.form_name || 'Unknown Form',
        formCode: draft.audit_form?.form_code || '',
        createdAt: draft.created_at,
        updatedAt: draft.updated_at,
        status: draft.status,
        scorePercentage: draft.score_percentage || 0,
        totalPointsAchieved: draft.total_points_achieved || 0,
        totalPointsPossible: draft.total_points_possible || 0,
        completedFieldsCount: draft.completed_fields_count || 0,
        shift: draft.shift || '',
        workOrder: draft.work_order || ''
      }));
      
      return {
        success: true,
        summary: summary,
        totalDrafts: summary.length
      };
    } catch (error) {
      console.error('❌ Error getting drafts summary:', error);
      return {
        success: false,
        error: 'Failed to get drafts summary',
        summary: []
      };
    }
  }

  // Delegate transformation methods to transformer
  transformCumulativeAuditDataToFieldResponses(cumulativeData, auditFormData) {
    return this.transformer.transformCumulativeAuditDataToFieldResponses(cumulativeData, auditFormData);
  }

  transformChecklistToFieldResponses(checklistData, auditFormData) {
    return this.transformer.transformChecklistToFieldResponses(checklistData, auditFormData);
  }

  isCumulativeDataFormat(data) {
    return this.transformer.isCumulativeDataFormat(data);
  }

  // Save audit progress (auto-save functionality)
  async saveProgress(responseId, progressData) {
    // Get current audit response to preserve existing raw_response_json
    const currentAudit = this.getCurrentAuditResponse();
    
    const updateData = {
      raw_response_json: {
        // PRESERVE existing raw_response_json content (including unit details)
        ...(currentAudit?.raw_response_json || {}),
        // Then add/update with new progress data
        ...progressData
      },
      total_points_achieved: progressData.totalPointsAchieved || 0,
      score_percentage: progressData.scorePercentage || 0
    };
    
    return await this.updateAuditResponse(responseId, updateData);
  }

  // Force save all pending data
  async forceSaveAll() {
    console.log('🚀 Force saving all pending independent audit data...');
    const responseIds = [...new Set(Array.from(this.saveQueue.values()).map(data => data.responseId))];
    
    const savePromises = responseIds.map(responseId => this.processSaveQueue(responseId));
    
    try {
      await Promise.all(savePromises);
      console.log('✅ All pending independent saves completed');
    } catch (error) {
      console.error('❌ Error in force save all:', error);
    }
  }

  // Get current audit response
  getCurrentAuditResponse() {
    return this.currentAuditResponse;
  }

  // Check if in draft mode
  getIsDraftMode() {
    return this.isDraftMode;
  }

  // Clear draft data with session awareness
  clearDraftData() {
    console.log('🧹 ENHANCED: Clearing independent draft data...');
    this.sessionManager.clearAllDraftData();
    this.currentAuditResponse = null;
    this.isDraftMode = false;
    this.saveQueue.clear();
    this.lastSaveTimestamp.clear();
    console.log('✅ Independent draft data cleared');
  }

  // Get device info for audit creation
  getDeviceInfo() {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    
    let deviceType = 'WEB_CLIENT';
    if (/Mobi|Android/i.test(userAgent)) {
      deviceType = 'MOBILE_WEB';
    } else if (/Tablet|iPad/i.test(userAgent)) {
      deviceType = 'TABLET_WEB';
    }
    
    return {
      device_id: `${deviceType}_${Date.now()}`,
      user_agent: userAgent,
      platform: platform,
      isIndependent: true
    };
  }

  // Delegate session management methods
  getAllStoredSessions() {
    return this.sessionManager.getAllStoredSessions();
  }

  switchToSession(sessionId) {
    return this.sessionManager.switchToSession(sessionId, this);
  }

  getSessionSummary(sessionId) {
    return this.sessionManager.getSessionSummary(sessionId);
  }

  exportAuditData(sessionId) {
    return this.sessionManager.exportAuditData(sessionId);
  }

  importAuditData(exportedData) {
    return this.sessionManager.importAuditData(exportedData, this);
  }

  cleanupOldSessions(maxAge) {
    return this.sessionManager.cleanupOldSessions(maxAge);
  }

  // Delegate performance monitoring methods
  getPerformanceMetrics() {
    return this.performanceMonitor.getPerformanceMetrics();
  }

  async healthCheck() {
    return await this.performanceMonitor.healthCheck();
  }

  // Delegate debugging methods
  analyzeDataStructure(data) {
    return this.debugger.analyzeDataStructure(data);
  }

  generateDebugReport(includeData = false) {
    return this.debugger.generateDebugReport(includeData);
  }

  async emergencyRecovery() {
    return await this.debugger.emergencyRecovery();
  }

  async verifyDataIntegrity() {
    return await this.debugger.verifyDataIntegrity();
  }

  // Validation methods
  validateFieldResponse(fieldResponse) {
    return this.transformer.validateFieldResponse(fieldResponse);
  }

  validateAllFieldResponses(fieldResponses) {
    return this.transformer.validateAllFieldResponses(fieldResponses);
  }

  // Get audit statistics
  getAuditStatistics() {
    return {
      currentAuditResponse: this.currentAuditResponse?.audit_form_response_id || null,
      isDraftMode: this.isDraftMode,
      pendingSaves: this.saveQueue.size,
      lastSaveCount: this.lastSaveTimestamp.size,
      isIndependent: this.independentMode,
      sessionId: this.sessionManager.getCurrentSessionId(),
      ...this.performanceMonitor.getPerformanceMetrics()
    };
  }

  // Get cache stats
  getCacheStats() {
    return this.transformer.getCacheStats();
  }

  // Clear all caches and reset service
  reset() {
    console.log('🔄 ENHANCED: Resetting independent audit service...');
    
    this.clearDraftData();
    this.saveQueue.clear();
    this.lastSaveTimestamp.clear();
    this.currentAuditResponse = null;
    this.isDraftMode = false;
    this.independentMode = true;
    
    // Reset sub-modules
    this.sessionManager.reset();
    this.performanceMonitor.reset();
    
    console.log('✅ Independent audit service reset complete');
  }
}

// Create and export a singleton instance
const auditService = new AuditService();

export default auditService;